// extension.js – Erweiterung zu einem bestehenden Stammbaum
// ═══════════════════════════════════════════════════════════════════════
// Idee: Ein Kunde hat schon ein Stammbaum-Memo (das „Original“) und möchte die
// Familie einer eingeheirateten Person dazunehmen (z.B. die Familie von Anias).
// Die Erweiterung ist ein eigener Stammbaum. Darin werden einige Personen aus dem
// Original übernommen („gesperrte“ Personen, grau dargestellt):
//   • Sie können nicht bearbeitet oder gelöscht werden.
//   • Sie behalten GENAU die Rahmenfarbe und die Symbole aus dem Original – ihre
//     Karten sind ja schon gedruckt und müssen zu den neuen Karten passen.
//   • Neue Familien bekommen nur Farben, die im Original noch nicht vorkommen.
// Bei jedem Öffnen der Erweiterung wird mit dem Original abgeglichen (syncExtension):
// Änderungen an Namen, Fotos, Farben oder Symbolen im Original werden übernommen.
//
// Gespeichert wird alles im Stammbaum selbst (data.extension):
//   baseCode, baseName, baseStatus – das Original
//   anchorId   – Ausgangsperson (z.B. Anias), deren Familie dazukommt
//   lockedIds  – übernommene Personen
//   lockedInfo – Farbe/Symbole dieser Personen im Original { id: { color, borderColors, symbols, outlineSymbol } }
//   baseColors – alle Familienfarben, die im Original vorkommen
//   syncedAt   – Zeitpunkt des letzten Abgleichs
import { computeColorSystem } from './colorSystem';

// Hilfstabellen: Eltern, Kinder, Partner einer Person
function maps(relationships) {
  const parents = new Map(), children = new Map(), partners = new Map();
  const add = (m, k, v) => { if (!m.has(k)) m.set(k, []); m.get(k).push(v); };
  relationships.forEach(r => {
    if (r.type === 'parent-child') { add(parents, r.to, r.from); add(children, r.from, r.to); }
    else if (r.type === 'partner' || r.type === 'ex-partner') { add(partners, r.from, r.to); add(partners, r.to, r.from); }
  });
  return { parents, children, partners };
}

// Wer kann Ausgangsperson sein? Alle, deren Eltern im Original fehlen
// (z.B. eingeheiratete Partner). Bei den anderen ist die Familie schon drin.
export function eligibleAnchors(base) {
  const { parents } = maps(base.relationships || []);
  return (base.persons || []).filter(p => !(parents.get(p.id) || []).length);
}

// Vorschlag, welche Personen übernommen werden: die Ausgangsperson, ihre Partner,
// alle ihre Nachkommen und deren Partner.
export function defaultSelection(base, anchorId) {
  const { children, partners } = maps(base.relationships || []);
  const sel = new Set([anchorId]);
  (partners.get(anchorId) || []).forEach(p => sel.add(p));
  const queue = [anchorId];
  while (queue.length) {
    const id = queue.shift();
    (children.get(id) || []).forEach(k => {
      if (sel.has(k) && k !== anchorId) return;
      sel.add(k);
      (partners.get(k) || []).forEach(p => sel.add(p));
      queue.push(k);
    });
  }
  return sel;
}

// Farben und Symbole der Personen im Original (so wie sie gedruckt werden)
function baseColorInfo(base) {
  const colors = computeColorSystem(base.persons || [], base.relationships || [], base.customColors || {}, base.extension || null);
  const info = {};
  const used = new Set();
  colors.forEach((ci, id) => {
    info[id] = {
      color: ci.color || null,
      borderColors: ci.borderColors || null,
      symbols: ci.symbols || null,
      outlineSymbol: ci.outlineSymbol || null,
    };
    if (ci.color) used.add(ci.color);
    (ci.borderColors || []).forEach(c => used.add(c));
  });
  return { info, used: [...used] };
}

// Neue Erweiterung aus dem Original bauen. selectedIds = übernommene Personen.
export function buildExtensionData(base, baseCode, baseName, baseStatus, anchorId, selectedIds) {
  const sel = new Set(selectedIds);
  sel.add(anchorId);
  const persons = (base.persons || []).filter(p => sel.has(p.id)).map(p => ({ ...p }));
  const relationships = (base.relationships || []).filter(r => sel.has(r.from) && sel.has(r.to)).map(r => ({ ...r }));
  const { info, used } = baseColorInfo(base);
  const lockedInfo = {};
  sel.forEach(id => { if (info[id]) lockedInfo[id] = info[id]; });
  return {
    persons,
    relationships,
    customColors: {},
    extension: {
      baseCode, baseName, baseStatus: baseStatus || 'erstellt',
      anchorId,
      lockedIds: [...sel],
      lockedInfo,
      baseColors: used,
      syncedAt: new Date().toISOString(),
    },
  };
}

// Abgleich mit dem Original (bei jedem Öffnen der Erweiterung):
//   • Daten der übernommenen Personen (Name, Daten, Foto …) aus dem Original
//   • Beziehungen zwischen übernommenen Personen aus dem Original
//   • Farben/Symbole der übernommenen Personen neu aus dem Original berechnet
// Personen, die im Original gelöscht wurden, bleiben mit ihrem letzten Stand erhalten.
// Gibt die neuen Daten zurück (oder dieselben, wenn sich nichts geändert hat).
export function syncExtension(data, base, baseMeta = {}) {
  const ext = data.extension;
  if (!ext) return data;
  const locked = new Set(ext.lockedIds || []);
  const baseById = new Map((base.persons || []).map(p => [p.id, p]));
  const persons = (data.persons || []).map(p => (locked.has(p.id) && baseById.has(p.id) ? { ...baseById.get(p.id) } : p));
  const inBase = id => baseById.has(id);
  const relationships = [
    // alle Beziehungen, die mindestens eine neue Person betreffen (oder eine gelöschte)
    ...(data.relationships || []).filter(r => !(locked.has(r.from) && locked.has(r.to) && inBase(r.from) && inBase(r.to))),
    // Beziehungen zwischen übernommenen Personen so wie im Original
    ...(base.relationships || []).filter(r => locked.has(r.from) && locked.has(r.to)).map(r => ({ ...r })),
  ];
  const { info, used } = baseColorInfo(base);
  const lockedInfo = { ...(ext.lockedInfo || {}) };
  locked.forEach(id => { if (info[id]) lockedInfo[id] = info[id]; });
  const next = {
    ...data,
    persons,
    relationships,
    extension: {
      ...ext,
      baseName: baseMeta.name || ext.baseName,
      baseStatus: baseMeta.status || ext.baseStatus,
      lockedInfo,
      baseColors: used,
    },
  };
  // Nur zurückgeben, wenn sich wirklich etwas geändert hat (sonst würde unnötig gespeichert).
  const strip = d => JSON.stringify({ p: d.persons, r: [...d.relationships].map(r => `${r.type}|${r.from}|${r.to}`).sort(), e: { ...d.extension, syncedAt: 0 } });
  if (strip(next) === strip(data)) return data;
  next.extension.syncedAt = new Date().toISOString();
  return next;
}

// Ist diese Person aus dem Original übernommen (grau, nicht bearbeitbar)?
export function isLocked(extension, id) {
  return !!(extension && (extension.lockedIds || []).includes(id));
}
